import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { BrandLogo } from "@/components/brand/BrandLogo";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import {
  ServicesPanel,
  ResourcesPanel,
  CoursesPanel,
} from "@/pages/admin/AtlasPanels";
import { SeoPanel } from "@/pages/admin/SeoPanel";
import { SiteContentPanel } from "@/pages/admin/SiteContentPanel";
import { GuidesPanel } from "@/pages/admin/GuidesPanel";
import { JourneyPanel } from "@/pages/admin/JourneyPanel";
import { FreeCallPanel } from "@/pages/admin/FreeCallPanel";
import {
  LogOut,
  Inbox,
  Star,
  FileText,
  BookOpen,
  Sparkles,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  ImagePlus,
  RefreshCw,
  Send,
  Search,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  DollarSign,
  Calendar as CalendarIcon,
  ClipboardList,
  GraduationCap,
  Route,
  Settings,
  KeyRound,
  UserPlus,
  Power,
} from "lucide-react";

const TABS = [
  { id: "clients", label: "Clients", icon: Inbox },
  { id: "freecall", label: "Free Call", icon: CalendarIcon },
  { id: "content", label: "Site Content", icon: FileText },
  { id: "services", label: "Services", icon: GraduationCap },
  { id: "journey", label: "Journey", icon: Route },
  { id: "resources", label: "Free Resources", icon: BookOpen },
  { id: "courses", label: "Courses", icon: Star },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "posts", label: "Journal", icon: FileText },
  { id: "seo", label: "SEO Pages", icon: Sparkles },
  { id: "guides", label: "Team Guides", icon: BookOpen },
  { id: "knowledge", label: "Knowledge", icon: BookOpen },
  { id: "agent", label: "Ask Agent", icon: Sparkles },
  { id: "settings", label: "Settings", icon: Settings },
];

const CLIENT_STATUSES = [
  { id: "new", label: "New", color: "#183129" },
  { id: "contacted", label: "Contacted", color: "#6B4954" },
  { id: "qualified", label: "Qualified", color: "#451C25" },
  { id: "discovery_scheduled", label: "Discovery scheduled", color: "#451C25" },
  { id: "discovery_done", label: "Discovery done", color: "#451C25" },
  { id: "proposal_sent", label: "Proposal sent", color: "#2B1017" },
  { id: "active", label: "Active client", color: "#451C25" },
  { id: "past", label: "Past client", color: "#6B4954" },
  { id: "lost", label: "Lost", color: "#9b8b7e" },
];
const STATUS_COLOR = Object.fromEntries(
  CLIENT_STATUSES.map((s) => [s.id, s.color])
);
const STATUS_LABEL = Object.fromEntries(
  CLIENT_STATUSES.map((s) => [s.id, s.label])
);

const VISA_PATHS = [
  "Undecided",
  "Spain NLV",
  "Spain Golden Visa",
  "Spain DNV",
  "Portugal D7",
  "Portugal D8",
  "Portugal Golden Visa",
  "Italy ERV",
  "Italy Investor",
  "Italy DNV",
  "Other",
];

const PLAN_OPTIONS = ["Undecided", "Essentials", "Guided", "Premium", "Custom"];
const INCOME_SOURCES = [
  "Social Security",
  "Pension",
  "Dividends",
  "Rental",
  "Annuity",
  "Other",
];
const LANG_LEVELS = ["none", "basic", "intermediate", "fluent"];

export default function AdminDashboard() {
  const { user, loading, logout, authedAxios, token } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("clients");

  useEffect(() => {
    if (!loading && !user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <main className="min-h-screen bg-[#F5EFE7] flex items-center justify-center text-[#6B4954]">
        Loading...
      </main>
    );
  }

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#F5EFE7] text-[#451C25]"
    >
      <Seo title="Admin | Destino Europe" description="Destino Europe admin." path="/admin/dashboard" noindex />
      <header className="sticky top-0 z-30 bg-[#F5EFE7]/90 backdrop-blur-xl border-b border-[#451C25]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="admin-brand">
            <BrandLogo variant="endorsement" size="sm" color="#451C25" markVariant="burgundy" subColor="#897756" />
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#6B4954] font-sans">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-[12px] text-[#6B4954]">
              {user.email}
            </span>
            <Link
              to="/admin/content"
              data-testid="admin-open-content-manager"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#451C25] hover:text-[#2B1017]"
            >
              <FileText size={14} /> Content Manager
            </Link>
            <button
              data-testid="admin-logout"
              onClick={() => {
                logout();
                navigate("/admin");
              }}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#451C25] hover:text-[#451C25]"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center gap-6 border-t border-[#451C25]/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 py-4 text-[12px] tracking-[0.22em] uppercase transition-colors ${
                  active ? "text-[#451C25]" : "text-[#451C25] hover:text-[#451C25]"
                }`}
              >
                <Icon size={14} />
                {t.label}
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#451C25]" />
                )}
              </button>
            );
          })}
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {tab === "clients" && <ClientsPanel axios={authedAxios} token={token} />}
        {tab === "freecall" && <FreeCallPanel axios={authedAxios} />}
        {tab === "services" && <ServicesPanel axios={authedAxios} />}
        {tab === "journey" && <JourneyPanel axios={authedAxios} />}
        {tab === "resources" && <ResourcesPanel axios={authedAxios} />}
        {tab === "courses" && <CoursesPanel axios={authedAxios} />}
        {tab === "reviews" && <ReviewsPanel axios={authedAxios} token={token} />}
        {tab === "posts" && <PostsPanel axios={authedAxios} token={token} />}
        {tab === "seo" && <SeoPanel axios={authedAxios} />}
        {tab === "content" && <SiteContentPanel axios={authedAxios} />}
        {tab === "guides" && <GuidesPanel axios={authedAxios} />}
        {tab === "knowledge" && <KnowledgePanel axios={authedAxios} />}
        {tab === "agent" && <AgentPanel axios={authedAxios} />}
        {tab === "settings" && (
          <SettingsPanel
            axios={authedAxios}
            currentEmail={user.email}
            onSelfRenamed={() => {
              toast.message("Your email was changed. Please sign in again.");
              logout();
              navigate("/admin");
            }}
          />
        )}
      </section>
    </main>
  );
}

/* ---------------- Clients (unified leads + active clients) ---------------- */
function ClientsPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("All");
  const [country, setCountry] = useState("All");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    const params = {};
    if (status !== "All") params.status = status;
    if (country !== "All") params.country = country;
    if (search.trim()) params.search = search.trim();
    axios
      .get(`/admin/consultations`, { params })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Could not load clients."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, country]);

  const counts = items.reduce((acc, c) => {
    acc[c.status] = (acc[c.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="clients-panel">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">Clients</h2>
          <p className="text-[#6B4954] text-sm mt-1">
            {items.length} records · click any row to open the full profile
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {CLIENT_STATUSES.map((s) => (
            <span
              key={s.id}
              className="px-2.5 py-1 bg-white border border-[#451C25]/10 inline-flex items-center gap-2"
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: s.color }}
              />
              {s.label}: <strong className="text-[#451C25]">{counts[s.id] || 0}</strong>
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <PillSelect
          label="Status"
          value={status}
          options={["All", ...CLIENT_STATUSES.map((s) => s.id)]}
          on={setStatus}
          testid="clients-filter-status"
          renderLabel={(o) => (o === "All" ? "All" : STATUS_LABEL[o] || o)}
        />
        <PillSelect
          label="Country"
          value={country}
          options={["All", "Spain", "Portugal", "Italy", "Undecided"]}
          on={setCountry}
          testid="clients-filter-country"
        />
        <div className="flex-1 min-w-[220px] flex items-center gap-2 border-b border-[#451C25]/20 focus-within:border-[#451C25]">
          <Search size={14} className="text-[#6B4954]" />
          <input
            data-testid="clients-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchAll()}
            placeholder="Search by name, email, or phone (Enter)"
            className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
      ) : items.length === 0 ? (
        <p className="text-[#6B4954] py-12 text-center bg-white border border-[#451C25]/10">
          No clients match these filters.
        </p>
      ) : (
        <div className="overflow-x-auto border border-[#451C25]/10 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#EDE4D6] text-left">
              <tr>
                <Th>Name</Th>
                <Th>Contact</Th>
                <Th>Pipeline</Th>
                <Th>Country / Visa</Th>
                <Th>Plan</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`client-row-${c.id}`}
                  onClick={() => setEditingId(c.id)}
                  className="border-t border-[#451C25]/10 hover:bg-[#F5EFE7] cursor-pointer"
                >
                  <Td>
                    <div className="font-medium">{c.full_name}</div>
                    <div className="text-[#6B4954] text-xs">
                      {c.us_state ? `${c.us_state}, US` : ""}
                      {c.profession ? ` · ${c.profession}` : ""}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-[#451C25] text-sm">{c.email}</div>
                    <div className="text-[#6B4954] text-xs">{c.phone || "—"}</div>
                  </Td>
                  <Td>
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider text-white"
                      style={{ background: STATUS_COLOR[c.status] || "#6B4954" }}
                    >
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    {c.next_action_date && (
                      <div className="text-[#6B4954] text-[10px] mt-1">
                        Next: {c.next_action_date}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div className="font-medium">{c.country_interest}</div>
                    <div className="text-[#6B4954] text-xs">
                      {c.visa_path_likely || "—"}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-[13px]">{c.plan_interest || "—"}</div>
                    {c.plan_purchased && (
                      <div className="text-[#451C25] text-[10px] uppercase tracking-wider">
                        Purchased: {c.plan_purchased}
                      </div>
                    )}
                  </Td>
                  <Td className="text-[#6B4954] text-xs whitespace-nowrap">
                    {new Date(c.updated_at || c.created_at).toLocaleDateString()}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingId && (
        <ClientDetailModal
          axios={axios}
          clientId={editingId}
          onClose={() => setEditingId(null)}
          onUpdated={fetchAll}
        />
      )}
    </div>
  );
}

/* ---------------- Client detail (full CRM profile) ---------------- */
function ClientDetailModal({ axios, clientId, onClose, onUpdated }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("profile");
  const [noteInput, setNoteInput] = useState("");
  const [conversations, setConversations] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/admin/consultations/${clientId}`);
      setClient(r.data);
    } catch {
      toast.error("Could not load client.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (tab === "conversations") {
      axios
        .get(`/admin/consultations/${clientId}/conversations`)
        .then((r) => setConversations(r.data || []))
        .catch(() => setConversations([]));
    }
  }, [tab, clientId, axios]);

  const updateField = (k, v) => setClient((c) => ({ ...c, [k]: v }));

  const toggleIncomeSource = (s) => {
    const list = client.income_sources || [];
    const next = list.includes(s)
      ? list.filter((x) => x !== s)
      : [...list, s];
    updateField("income_sources", next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...client };
      delete payload.id;
      delete payload.notes;
      delete payload.created_at;
      delete payload.updated_at;
      await axios.patch(`/admin/consultations/${clientId}`, payload);
      toast.success("Client saved.");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    const text = noteInput.trim();
    if (!text) return;
    try {
      const r = await axios.post(`/admin/consultations/${clientId}/notes`, {
        content: text,
      });
      setClient(r.data);
      setNoteInput("");
      onUpdated?.();
    } catch {
      toast.error("Could not save note.");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      const r = await axios.delete(
        `/admin/consultations/${clientId}/notes/${noteId}`
      );
      setClient(r.data);
      onUpdated?.();
    } catch {
      toast.error("Could not delete note.");
    }
  };

  return (
    <div
      data-testid="client-detail"
      className="fixed inset-0 z-50 bg-[#451C25]/55 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
    >
      <div className="w-full max-w-5xl bg-[#F5EFE7] border border-[#451C25]/15 my-6">
        <header className="flex items-center justify-between px-8 py-5 border-b border-[#451C25]/10 sticky top-0 bg-[#F5EFE7] z-10">
          <div>
            <div className="font-serif text-2xl tracking-tight">
              {client?.full_name || "Loading..."}
            </div>
            <div className="text-[#6B4954] text-xs">{client?.email}</div>
          </div>
          <div className="flex items-center gap-4">
            {client && (
              <select
                data-testid="client-status-select"
                value={client.status || "new"}
                onChange={(e) => updateField("status", e.target.value)}
                className="bg-white border border-[#451C25]/15 px-3 py-2 text-[12px] uppercase tracking-[0.15em]"
              >
                {CLIENT_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            )}
            <button
              data-testid="client-save"
              onClick={save}
              disabled={saving || !client}
              className="inline-flex items-center gap-2 bg-[#451C25] disabled:opacity-50 text-white px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
            >
              <Save size={14} /> {saving ? "Saving..." : "Save"}
            </button>
            <button
              data-testid="client-close"
              onClick={onClose}
              className="text-[#6B4954] hover:text-[#451C25]"
            >
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="flex items-center gap-5 px-8 border-b border-[#451C25]/10">
          {[
            { id: "profile", label: "Profile", icon: User },
            { id: "financial", label: "Financial", icon: DollarSign },
            { id: "relocation", label: "Relocation", icon: MapPin },
            { id: "pipeline", label: "Pipeline", icon: ClipboardList },
            { id: "notes", label: `Notes (${client?.notes?.length || 0})`, icon: FileText },
            { id: "conversations", label: "Agent history", icon: Sparkles },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`client-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 py-3 text-[11px] tracking-[0.18em] uppercase transition-colors ${
                  active ? "text-[#451C25]" : "text-[#451C25] hover:text-[#451C25]"
                }`}
              >
                <Icon size={12} />
                {t.label}
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#451C25]" />
                )}
              </button>
            );
          })}
        </div>

        <div className="p-8">
          {loading || !client ? (
            <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
          ) : tab === "profile" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <TF label="Full name" value={client.full_name} on={(v) => updateField("full_name", v)} testid="cf-name" />
              <TF label="Email" value={client.email} on={(v) => updateField("email", v)} testid="cf-email" />
              <TF label="Phone" value={client.phone || ""} on={(v) => updateField("phone", v)} testid="cf-phone" />
              <TF label="Date of birth (YYYY-MM-DD)" value={client.date_of_birth || ""} on={(v) => updateField("date_of_birth", v)} testid="cf-dob" />
              <TF label="US state" value={client.us_state || ""} on={(v) => updateField("us_state", v)} testid="cf-state" placeholder="FL, TX, NY..." />
              <TF label="Profession" value={client.profession || ""} on={(v) => updateField("profession", v)} testid="cf-profession" />
              <TF label="Spouse name" value={client.spouse_name || ""} on={(v) => updateField("spouse_name", v)} testid="cf-spouse" />
              <TF label="Dependents (count)" value={String(client.dependents_count || 0)} on={(v) => updateField("dependents_count", parseInt(v, 10) || 0)} testid="cf-deps" />
              <div className="md:col-span-2">
                <TF label="Dependents notes" value={client.dependents_notes || ""} on={(v) => updateField("dependents_notes", v)} testid="cf-deps-notes" />
              </div>
              <SelectField
                label="Target language level"
                value={client.speaks_target_language || "none"}
                options={LANG_LEVELS}
                on={(v) => updateField("speaks_target_language", v)}
                testid="cf-lang"
              />
            </div>
          ) : tab === "financial" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <TF label="Annual passive income (USD)" value={client.annual_passive_income_usd || ""} on={(v) => updateField("annual_passive_income_usd", parseFloat(v) || null)} testid="cf-income" />
              <TF label="Liquid savings (USD)" value={client.liquid_savings_usd || ""} on={(v) => updateField("liquid_savings_usd", parseFloat(v) || null)} testid="cf-savings" />
              <TF label="Estimated net worth (USD)" value={client.net_worth_estimate_usd || ""} on={(v) => updateField("net_worth_estimate_usd", parseFloat(v) || null)} testid="cf-networth" />
              <TF label="Target monthly budget (USD)" value={client.target_budget_monthly_usd || ""} on={(v) => updateField("target_budget_monthly_usd", parseFloat(v) || null)} testid="cf-budget" />
              <div className="md:col-span-2">
                <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-3">
                  Income sources
                </label>
                <div className="flex flex-wrap gap-2">
                  {INCOME_SOURCES.map((s) => {
                    const active = (client.income_sources || []).includes(s);
                    return (
                      <button
                        key={s}
                        data-testid={`cf-income-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleIncomeSource(s)}
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                          active
                            ? "bg-[#451C25] text-white border-[#451C25]"
                            : "border-[#451C25]/25 text-[#451C25] hover:border-[#451C25]"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : tab === "relocation" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SelectField
                label="Country of interest"
                value={client.country_interest || "Undecided"}
                options={["Spain", "Portugal", "Italy", "Undecided"]}
                on={(v) => updateField("country_interest", v)}
                testid="cf-country"
              />
              <SelectField
                label="Likely visa path"
                value={client.visa_path_likely || "Undecided"}
                options={VISA_PATHS}
                on={(v) => updateField("visa_path_likely", v)}
                testid="cf-visa"
              />
              <TF label="City / area interests" value={client.city_interests || ""} on={(v) => updateField("city_interests", v)} testid="cf-cities" placeholder="Valencia, Algarve, Lucca..." />
              <TF label="Target move date" value={client.target_move_date || ""} on={(v) => updateField("target_move_date", v)} testid="cf-movedate" placeholder="YYYY-MM" />
              <SelectField label="Already owns property in target country?" value={client.has_property_already ? "Yes" : "No"} options={["No", "Yes"]} on={(v) => updateField("has_property_already", v === "Yes")} testid="cf-prop" />
              <SelectField label="Bringing pets?" value={client.bringing_pets ? "Yes" : "No"} options={["No", "Yes"]} on={(v) => updateField("bringing_pets", v === "Yes")} testid="cf-pets" />
              <SelectField label="Special needs in family?" value={client.has_special_needs ? "Yes" : "No"} options={["No", "Yes"]} on={(v) => updateField("has_special_needs", v === "Yes")} testid="cf-needs" />
              <div className="md:col-span-2">
                <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">Original lead message</label>
                <div className="bg-white border border-[#451C25]/15 p-3 text-[14px] text-[#6B4954] whitespace-pre-wrap min-h-[80px]">
                  {client.message || "—"}
                </div>
              </div>
            </div>
          ) : tab === "pipeline" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <SelectField label="Plan interest" value={client.plan_interest || "Undecided"} options={PLAN_OPTIONS} on={(v) => updateField("plan_interest", v)} testid="cf-plan" />
              <SelectField label="Plan purchased" value={client.plan_purchased || ""} options={["", ...PLAN_OPTIONS.filter((p) => p !== "Undecided")]} on={(v) => updateField("plan_purchased", v || null)} testid="cf-plan-paid" />
              <TF label="Assigned advisor" value={client.assigned_advisor || ""} on={(v) => updateField("assigned_advisor", v)} testid="cf-advisor" placeholder="advisor@destinoeurope.com" />
              <TF label="Lead source" value={client.lead_source || "website"} on={(v) => updateField("lead_source", v)} testid="cf-source" />
              <TF label="Last contact date" value={client.last_contact_date || ""} on={(v) => updateField("last_contact_date", v)} testid="cf-lastcontact" placeholder="YYYY-MM-DD" />
              <TF label="Next action date" value={client.next_action_date || ""} on={(v) => updateField("next_action_date", v)} testid="cf-nextdate" placeholder="YYYY-MM-DD" />
              <div className="md:col-span-2">
                <TF label="Next action" value={client.next_action_note || ""} on={(v) => updateField("next_action_note", v)} testid="cf-nextaction" placeholder="Send tax briefing email" />
              </div>
            </div>
          ) : tab === "notes" ? (
            <div>
              <div className="flex gap-3 mb-6">
                <textarea
                  data-testid="cf-note-input"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  rows={3}
                  placeholder="Add a timeline note — what you discussed, decisions, next steps..."
                  className="flex-1 bg-white border border-[#451C25]/15 p-3 text-sm focus:outline-none focus:border-[#451C25]"
                />
                <button
                  data-testid="cf-note-add"
                  onClick={addNote}
                  className="self-stretch inline-flex items-center gap-2 bg-[#451C25] text-white px-5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
                >
                  <Plus size={14} /> Add note
                </button>
              </div>
              <div className="space-y-3">
                {(client.notes || [])
                  .slice()
                  .reverse()
                  .map((n) => (
                    <div
                      key={n.id}
                      data-testid={`cf-note-${n.id}`}
                      className="bg-white border border-[#451C25]/10 p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-[#451C25]">
                          {n.author_email}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[#6B4954] text-xs">
                            {new Date(n.created_at).toLocaleString()}
                          </span>
                          <button
                            onClick={() => deleteNote(n.id)}
                            className="text-[#6B4954] hover:text-[#451C25]"
                            aria-label="Delete note"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <p className="text-[14px] whitespace-pre-wrap leading-relaxed">
                        {n.content}
                      </p>
                    </div>
                  ))}
                {(client.notes || []).length === 0 && (
                  <p className="text-[#6B4954] text-sm text-center py-8 bg-white border border-[#451C25]/10">
                    No notes yet.
                  </p>
                )}
              </div>
            </div>
          ) : tab === "conversations" ? (
            <div>
              {conversations.length === 0 ? (
                <p className="text-[#6B4954] text-sm text-center py-8 bg-white border border-[#451C25]/10">
                  No agent conversations linked to this client yet. Start one from
                  the <strong>Ask Agent</strong> tab and select this client.
                </p>
              ) : (
                <div className="space-y-3">
                  {conversations.map((c) => (
                    <div
                      key={c.session_id}
                      className="bg-white border border-[#451C25]/10 p-4"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-[#451C25]">
                          {c.message_count} messages
                        </div>
                        <span className="text-[#6B4954] text-xs">
                          {new Date(c.last_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-[13px] text-[#6B4954]">"{c.preview}"</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
const EMPTY_REVIEW = {
  name: "",
  location: "",
  country: "Spain",
  plan: "Guided",
  rating: 5,
  quote: "",
  photo_url: "",
  video_url: "",
  featured: true,
};

function ReviewsPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or review obj (with id) or 'new'

  const fetchAll = () => {
    setLoading(true);
    axios
      .get(`/admin/reviews`)
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Could not load reviews."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (data) => {
    try {
      if (editing && editing !== "new") {
        await axios.put(`/admin/reviews/${editing.id}`, data);
        toast.success("Review updated.");
      } else {
        await axios.post(`/admin/reviews`, data);
        toast.success("Review created.");
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`/admin/reviews/${id}`);
      toast.success("Deleted.");
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="reviews-panel">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">Reviews</h2>
          <p className="text-[#6B4954] text-sm mt-1">
            {items.length} reviews · public ones appear on the landing
          </p>
        </div>
        <button
          data-testid="review-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
        >
          <Plus size={14} /> New review
        </button>
      </div>

      {loading ? (
        <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`review-row-${r.id}`}
              className="bg-white border border-[#451C25]/10 p-5 flex gap-4"
            >
              {r.photo_url ? (
                <img
                  src={r.photo_url}
                  alt={r.name}
                  className="w-14 h-14 object-cover rounded-full shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#EDE4D6] shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        className={
                          i < r.rating
                            ? "fill-[#451C25] text-[#451C25]"
                            : "text-[#451C25]/20"
                        }
                      />
                    ))}
                  </div>
                </div>
                <div className="text-[#6B4954] text-xs mb-2">
                  {r.location} · {r.country} · {r.plan}
                </div>
                <p className="text-[14px] text-[#451C25]/85 leading-relaxed line-clamp-3">
                  "{r.quote}"
                </p>
                <div className="flex items-center gap-3 mt-3">
                  <button
                    data-testid={`review-edit-${r.id}`}
                    onClick={() => setEditing(r)}
                    className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#451C25] hover:text-[#451C25]"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#6B4954] hover:text-[#451C25]"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ReviewEditor
          initial={editing === "new" ? EMPTY_REVIEW : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function ReviewEditor({ initial, onClose, onSave }) {
  const [data, setData] = useState({ ...EMPTY_REVIEW, ...initial });
  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div
      data-testid="review-editor"
      className="fixed inset-0 z-50 bg-[#451C25]/55 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="w-full max-w-2xl bg-[#F5EFE7] border border-[#451C25]/15 p-8 my-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl tracking-tight">
            {initial?.id ? "Edit review" : "New review"}
          </h3>
          <button
            onClick={onClose}
            data-testid="review-editor-close"
            className="text-[#6B4954] hover:text-[#451C25]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <TF label="Name *" value={data.name} on={(v) => upd("name", v)} testid="rev-name" />
          <TF label="Location *" value={data.location} on={(v) => upd("location", v)} testid="rev-location" />
          <SelectField
            label="Country *"
            value={data.country}
            options={["Spain", "Portugal", "Italy"]}
            on={(v) => upd("country", v)}
            testid="rev-country"
          />
          <SelectField
            label="Plan *"
            value={data.plan}
            options={["Essentials", "Guided", "Premium"]}
            on={(v) => upd("plan", v)}
            testid="rev-plan"
          />
          <SelectField
            label="Rating *"
            value={String(data.rating)}
            options={["5", "4", "3", "2", "1"]}
            on={(v) => upd("rating", parseInt(v, 10))}
            testid="rev-rating"
          />
          <SelectField
            label="Featured"
            value={data.featured ? "Yes" : "No"}
            options={["Yes", "No"]}
            on={(v) => upd("featured", v === "Yes")}
            testid="rev-featured"
          />
          <TF
            label="Photo URL"
            value={data.photo_url || ""}
            on={(v) => upd("photo_url", v)}
            testid="rev-photo"
            placeholder="https://..."
          />
          <TF
            label="Video URL"
            value={data.video_url || ""}
            on={(v) => upd("video_url", v)}
            testid="rev-video"
            placeholder="https://..."
          />
        </div>

        <div className="mt-5">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Quote *
          </label>
          <textarea
            data-testid="rev-quote"
            value={data.quote}
            onChange={(e) => upd("quote", e.target.value)}
            rows={4}
            className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25] transition-colors"
          />
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="review-save"
            onClick={() => onSave(data)}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
          >
            <Save size={14} /> Save review
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Posts ---------------- */
const EMPTY_POST = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  cover_url: "",
  cover_alt: "",
  country_tag: "All",
  category: "Visas",
  read_time: 6,
  published: true,
  route_tags: [],
  seo_title: "",
  meta_description: "",
  last_reviewed: "",
};
const ROUTE_TAG_OPTIONS = ["general", "work", "retire", "home"];

function PostsPanel({ axios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    axios
      .get(`/admin/posts`)
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Could not load posts."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (data) => {
    try {
      if (editing && editing !== "new") {
        await axios.put(`/admin/posts/${editing.id}`, data);
        toast.success("Post updated.");
      } else {
        await axios.post(`/admin/posts`, data);
        toast.success("Post created.");
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`/admin/posts/${id}`);
      toast.success("Deleted.");
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="posts-panel">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">Journal</h2>
          <p className="text-[#6B4954] text-sm mt-1">
            {items.length} posts · public ones appear at /blog
          </p>
        </div>
        <button
          data-testid="post-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
        >
          <Plus size={14} /> New post
        </button>
      </div>

      {loading ? (
        <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
      ) : (
        <div className="border border-[#451C25]/10 bg-white divide-y divide-[#451C25]/10">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`post-row-${p.id}`}
              className="p-5 flex items-center gap-4 hover:bg-[#F5EFE7]"
            >
              {p.cover_url ? (
                <img
                  src={p.cover_url}
                  alt=""
                  className="w-16 h-12 object-cover shrink-0"
                />
              ) : (
                <div className="w-16 h-12 bg-[#EDE4D6] shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.title}</div>
                <div className="text-[#6B4954] text-xs">
                  /blog/{p.slug} · {p.country_tag} · {p.category}
                  {!p.published && " · DRAFT"}
                  {p.editorial_status === "review" && <span className="ml-2 px-1.5 py-0.5 bg-[#B3A17D]/30 text-[#451C25] uppercase tracking-wider text-[10px]">Review figures</span>}
                </div>
              </div>
              <button
                data-testid={`post-edit-${p.id}`}
                onClick={() => setEditing(p)}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#451C25] hover:text-[#451C25]"
              >
                <Pencil size={12} /> Edit
              </button>
              <button
                data-testid={`post-delete-${p.id}`}
                onClick={() => remove(p.id)}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#6B4954] hover:text-[#451C25]"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PostEditor
          initial={editing === "new" ? EMPTY_POST : editing}
          onClose={() => setEditing(null)}
          onSave={save}
          axios={axios}
        />
      )}
    </div>
  );
}

function PostEditor({ initial, onClose, onSave, axios }) {
  const [data, setData] = useState({ ...EMPTY_POST, ...initial });
  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div
      data-testid="post-editor"
      className="fixed inset-0 z-50 bg-[#451C25]/55 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4"
    >
      <div className="w-full sm:max-w-3xl bg-[#F5EFE7] sm:border border-[#451C25]/15 flex flex-col h-full sm:h-auto max-h-[100dvh] sm:max-h-[90vh]">
        <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-[#451C25]/10 shrink-0">
          <h3 className="font-serif text-xl sm:text-2xl tracking-tight">
            {initial?.id ? "Edit post" : "New post"}
          </h3>
          <button
            onClick={onClose}
            data-testid="post-editor-close"
            className="text-[#6B4954] hover:text-[#451C25]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 sm:px-8 py-5 sm:py-6 flex-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <TF label="Title *" value={data.title} on={(v) => upd("title", v)} testid="post-title" />
          <TF
            label="Slug * (e.g. spain-non-lucrative-visa)"
            value={data.slug}
            on={(v) => upd("slug", v)}
            testid="post-slug"
          />
          <SelectField
            label="Country tag"
            value={data.country_tag}
            options={["All", "Spain", "Portugal", "Italy"]}
            on={(v) => upd("country_tag", v)}
            testid="post-country"
          />
          <SelectField
            label="Category"
            value={data.category}
            options={["Visas", "Tax", "Healthcare", "Banking", "Lifestyle", "Property", "Legal"]}
            on={(v) => upd("category", v)}
            testid="post-category"
          />
          <TF
            label="Read time (min)"
            value={String(data.read_time || 6)}
            on={(v) => upd("read_time", parseInt(v, 10) || 6)}
            testid="post-readtime"
          />
        </div>

        <div className="mt-5">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Excerpt *
          </label>
          <textarea
            data-testid="post-excerpt"
            value={data.excerpt}
            onChange={(e) => upd("excerpt", e.target.value)}
            rows={2}
            className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25] transition-colors"
          />
        </div>

        <CoverImageField
          postId={initial?.id}
          slug={data.slug}
          value={data.cover_url || ""}
          alt={data.cover_alt || ""}
          onChange={(v) => upd("cover_url", v)}
          onAltChange={(v) => upd("cover_alt", v)}
          axios={axios}
        />

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="post-seo-fields">
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">Route tags (country + route)</label>
            <div className="flex flex-wrap gap-2">
              {ROUTE_TAG_OPTIONS.map((t) => {
                const on = (data.route_tags || []).includes(t);
                return (
                  <button key={t} type="button" data-testid={`post-route-${t}`} aria-pressed={on} onClick={() => upd("route_tags", on ? (data.route_tags || []).filter((x) => x !== t) : [...(data.route_tags || []), t])} className={`text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 border ${on ? "bg-[#451C25] text-white border-[#451C25]" : "border-[#451C25]/30 text-[#451C25]"}`}>{t}</button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">Last reviewed (YYYY-MM-DD)</label>
            <input data-testid="post-last-reviewed" value={data.last_reviewed || ""} onChange={(e) => upd("last_reviewed", e.target.value)} className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25]" />
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">SEO title (fallback: title)</label>
            <input data-testid="post-seo-title" value={data.seo_title || ""} onChange={(e) => upd("seo_title", e.target.value)} className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25]" />
          </div>
          <div>
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">Meta description (fallback: excerpt)</label>
            <textarea data-testid="post-meta-description" value={data.meta_description || ""} onChange={(e) => upd("meta_description", e.target.value)} rows={2} className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25]" />
          </div>
        </div>

        <div className="mt-5">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Body * — WYSIWYG editor (supports headings, bold, lists, links)
          </label>
          <div data-testid="post-body" data-color-mode="light">
            <MDEditor
              value={data.body}
              onChange={(v) => upd("body", v || "")}
              height={520}
              preview="live"
              visibleDragbar={false}
              textareaProps={{
                placeholder:
                  "Start writing... Use the toolbar above for headings, bold, lists, links, etc.",
              }}
            />
          </div>
          <p className="mt-2 text-[11px] text-[#6B4954]">
            Tip: Use <code>##</code> for section titles, <code>**bold**</code> for emphasis,
            and <code>- item</code> for bullet points. Switch to "Preview" to see how it
            will render on the public site.
          </p>
        </div>

        <div className="mt-5">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              data-testid="post-published"
              type="checkbox"
              checked={data.published}
              onChange={(e) => upd("published", e.target.checked)}
              className="accent-[#451C25]"
            />
            Published (visible on /blog)
          </label>
        </div>
        </div>

        <div className="flex justify-end gap-3 px-5 sm:px-8 py-4 border-t border-[#451C25]/10 shrink-0 bg-[#F5EFE7]">
          <button
            onClick={onClose}
            className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="post-save"
            onClick={() => onSave(data)}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
          >
            <Save size={14} /> Save post
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tiny helpers ---------------- */
function Th({ children }) {
  return (
    <th className="px-4 py-3 text-[10px] tracking-[0.22em] uppercase text-[#6B4954] font-medium">
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function TF({ label, value, on, testid, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#451C25]/20 py-2 focus:outline-none focus:border-[#451C25] transition-colors"
      />
    </div>
  );
}
function SelectField({ label, value, options, on, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-transparent border-b border-[#451C25]/20 py-2 focus:outline-none focus:border-[#451C25] transition-colors"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function formatBytes(n) {
  if (n === null || n === undefined) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const COVER_ACCEPT = "image/jpeg,image/png,image/webp";
const COVER_MAX_BYTES = 10 * 1024 * 1024;

// Reusable Journal cover-image workflow: upload / drag-drop / preview / replace / remove + alt text.
function CoverImageField({ postId, slug, value, alt, onChange, onAltChange, axios }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [meta, setMeta] = useState(null);

  const pick = () => inputRef.current && inputRef.current.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!postId) {
      toast.error("Save the draft first, then upload a cover image.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Unsupported file type. Use JPG, PNG or WebP.");
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      toast.error("Image is larger than 10 MB. Please upload a smaller file.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    if (alt) fd.append("alt", alt);
    setUploading(true);
    try {
      const r = await axios.post(`/admin/posts/${postId}/cover`, fd);
      onChange(r.data.cover_url);
      setMeta({ filename: r.data.filename, width: r.data.width, height: r.data.height, size: r.data.size });
      toast.success("Cover image uploaded.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed — the previous cover was kept.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(f);
  };

  const removeCover = async () => {
    if (!postId) {
      onChange("");
      onAltChange("");
      setMeta(null);
      return;
    }
    try {
      await axios.delete(`/admin/posts/${postId}/cover`);
      onChange("");
      onAltChange("");
      setMeta(null);
      toast.success("Cover image removed.");
    } catch {
      toast.error("Could not remove the cover.");
    }
  };

  return (
    <div className="mt-5" data-testid="cover-image-field">
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">Cover image</label>
      <input
        ref={inputRef}
        type="file"
        accept={COVER_ACCEPT}
        className="hidden"
        data-testid="cover-upload-input"
        onChange={(e) => handleFile(e.target.files && e.target.files[0])}
      />

      {!value ? (
        <div
          data-testid="cover-dropzone"
          role="button"
          tabIndex={0}
          onClick={pick}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && pick()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`cursor-pointer border border-dashed p-6 text-center transition-colors ${
            dragOver ? "border-[#451C25] bg-[#451C25]/5" : "border-[#451C25]/30 hover:border-[#451C25]/60"
          }`}
        >
          <ImagePlus size={22} className="mx-auto text-[#897756]" aria-hidden="true" />
          <p className="mt-3 text-sm text-[#451C25]">
            <button
              type="button"
              data-testid="cover-upload-btn"
              onClick={(e) => {
                e.stopPropagation();
                pick();
              }}
              className="font-semibold underline underline-offset-4"
            >
              {uploading ? "Uploading…" : "Upload image"}
            </button>{" "}
            or drag &amp; drop
          </p>
          <p className="mt-1 text-[11px] text-[#6B4954]">
            JPG, PNG or WebP · up to 10&nbsp;MB · used on the article page, Resources and related-content cards.
          </p>
          {!postId && (
            <p className="mt-2 text-[11px] text-[#9b5d4e]" data-testid="cover-needs-save">
              Save the draft first to enable cover upload.
            </p>
          )}
        </div>
      ) : (
        <div className="border border-[#451C25]/15 bg-white p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-56 shrink-0 aspect-[3/2] overflow-hidden bg-[#EDE4D6]">
              <img data-testid="cover-preview-img" src={value} alt={alt || ""} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              {meta ? (
                <div className="text-[13px] text-[#451C25] space-y-0.5">
                  <div className="truncate" data-testid="cover-filename">{meta.filename}</div>
                  <div className="text-[#6B4954]">
                    {meta.width}×{meta.height}px{meta.size ? ` · ${formatBytes(meta.size)}` : ""}
                  </div>
                </div>
              ) : (
                <div className="text-[13px] text-[#6B4954] break-all" data-testid="cover-current-url">
                  {String(value).split("?")[0]}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="cover-replace-btn"
                  onClick={pick}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[#451C25]/30 px-3 py-2 hover:bg-[#451C25] hover:text-white transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} /> {uploading ? "Uploading…" : "Replace image"}
                </button>
                <button
                  type="button"
                  data-testid="cover-remove-btn"
                  onClick={removeCover}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[#451C25]/30 px-3 py-2 text-[#6B4954] hover:bg-[#451C25] hover:text-white transition-colors disabled:opacity-50"
                >
                  <Trash2 size={12} /> Remove image
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">Image alt text</label>
            <input
              data-testid="cover-alt-input"
              value={alt || ""}
              onChange={(e) => onAltChange(e.target.value)}
              className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25]"
            />
            <p className="mt-1 text-[11px] text-[#6B4954]">
              Describe the image briefly for accessibility. Leave empty only when the image is purely decorative.
            </p>
          </div>
        </div>
      )}

      <details className="mt-3">
        <summary
          data-testid="cover-advanced-toggle"
          className="cursor-pointer text-[11px] tracking-[0.18em] uppercase text-[#6B4954] hover:text-[#451C25]"
        >
          Advanced: set image URL manually
        </summary>
        <div className="mt-3">
          <TF label="Cover image URL" value={value || ""} on={onChange} testid="post-cover" placeholder="https://… or /img/articles/…" />
          <p className="mt-1 text-[11px] text-[#6B4954]">For legacy assets only. Normal uploads are handled above.</p>
        </div>
      </details>
    </div>
  );
}


/* ============================================================
   KNOWLEDGE PANEL
   ============================================================ */
const COUNTRIES_KB = ["Spain", "Portugal", "Italy", "Cross-border"];
const TOPICS_KB = [
  "Visas",
  "Tax",
  "Healthcare",
  "Banking",
  "Property",
  "Cost of Living",
  "Pets",
  "Driving",
  "Inheritance",
  "Education",
  "Citizenship",
  "Other",
];

const EMPTY_KB = {
  country: "Spain",
  topic: "Visas",
  title: "",
  body: "",
  tags: [],
  source_url: "",
};

function KnowledgePanel({ axios }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [country, setCountry] = useState("All");
  const [topic, setTopic] = useState("All");
  const [search, setSearch] = useState("");

  const fetchAll = () => {
    setLoading(true);
    const params = {};
    if (country !== "All") params.country = country;
    if (topic !== "All") params.topic = topic;
    if (search) params.search = search;
    axios
      .get(`/admin/knowledge`, { params })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Could not load knowledge."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, topic]);

  const onSave = async (data) => {
    try {
      const payload = { ...data, tags: data.tags || [] };
      if (editing && editing !== "new") {
        await axios.put(`/admin/knowledge/${editing.id}`, payload);
        toast.success("Article updated.");
      } else {
        await axios.post(`/admin/knowledge`, payload);
        toast.success("Article created.");
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article? It will no longer be used by the agent.")) return;
    try {
      await axios.delete(`/admin/knowledge/${id}`);
      toast.success("Deleted.");
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="knowledge-panel">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">Knowledge base</h2>
          <p className="text-[#6B4954] text-sm mt-1">
            {items.length} articles · used by the Ask Agent panel
          </p>
        </div>
        <button
          data-testid="kb-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors self-start"
        >
          <Plus size={14} /> New article
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <PillSelect
          label="Country"
          value={country}
          options={["All", ...COUNTRIES_KB]}
          on={setCountry}
          testid="kb-filter-country"
        />
        <PillSelect
          label="Topic"
          value={topic}
          options={["All", ...TOPICS_KB]}
          on={setTopic}
          testid="kb-filter-topic"
        />
        <div className="flex-1 min-w-[200px] flex items-center gap-2 border-b border-[#451C25]/20 focus-within:border-[#451C25]">
          <Search size={14} className="text-[#6B4954]" />
          <input
            data-testid="kb-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchAll()}
            placeholder="Search title, body, tags... (Enter to search)"
            className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
      ) : items.length === 0 ? (
        <p className="text-[#6B4954] py-12 text-center bg-white border border-[#451C25]/10">
          No articles match these filters.
        </p>
      ) : (
        <div className="border border-[#451C25]/10 bg-white divide-y divide-[#451C25]/10">
          {items.map((a) => (
            <div
              key={a.id}
              data-testid={`kb-row-${a.id}`}
              className="p-5 flex items-start gap-4 hover:bg-[#F5EFE7]"
            >
              <div className="shrink-0 flex flex-col items-center gap-1.5 w-24">
                <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#451C25] text-white">
                  {a.country}
                </span>
                <span className="px-2 py-0.5 text-[10px] tracking-wider uppercase bg-[#EDE4D6]">
                  {a.topic}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium leading-tight mb-1">{a.title}</div>
                <p className="text-[#6B4954] text-[13px] leading-snug line-clamp-2">
                  {a.body}
                </p>
                {a.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {a.tags.map((t) => (
                      <span
                        key={t}
                        className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#EDE4D6] text-[#6B4954]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex flex-col gap-2">
                <button
                  data-testid={`kb-edit-${a.id}`}
                  onClick={() => setEditing(a)}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#451C25] hover:text-[#451C25]"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  data-testid={`kb-delete-${a.id}`}
                  onClick={() => remove(a.id)}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[#6B4954] hover:text-[#451C25]"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <KnowledgeEditor
          initial={editing === "new" ? EMPTY_KB : editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function KnowledgeEditor({ initial, onClose, onSave }) {
  const [data, setData] = useState({
    ...EMPTY_KB,
    ...initial,
    tags: initial.tags || [],
  });
  const [tagInput, setTagInput] = useState("");
  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || data.tags.includes(t)) return;
    upd("tags", [...data.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => upd("tags", data.tags.filter((x) => x !== t));

  return (
    <div
      data-testid="kb-editor"
      className="fixed inset-0 z-50 bg-[#451C25]/55 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="w-full max-w-3xl bg-[#F5EFE7] border border-[#451C25]/15 p-8 my-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl tracking-tight">
            {initial?.id ? "Edit article" : "New article"}
          </h3>
          <button
            onClick={onClose}
            data-testid="kb-editor-close"
            className="text-[#6B4954] hover:text-[#451C25]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <SelectField
            label="Country *"
            value={data.country}
            options={COUNTRIES_KB}
            on={(v) => upd("country", v)}
            testid="kb-country"
          />
          <SelectField
            label="Topic *"
            value={data.topic}
            options={TOPICS_KB}
            on={(v) => upd("topic", v)}
            testid="kb-topic"
          />
        </div>

        <div className="mt-5">
          <TF label="Title *" value={data.title} on={(v) => upd("title", v)} testid="kb-title" />
        </div>

        <div className="mt-5">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Body * (Markdown-lite: `## Heading`, `**bold**`, `• bullet`)
          </label>
          <textarea
            data-testid="kb-body"
            value={data.body}
            onChange={(e) => upd("body", e.target.value)}
            rows={16}
            className="w-full bg-transparent border border-[#451C25]/20 p-3 focus:outline-none focus:border-[#451C25] transition-colors font-mono text-[13px]"
          />
        </div>

        <div className="mt-5">
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Tags (for retrieval)
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {data.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-wider bg-[#EDE4D6]"
              >
                {t}
                <button
                  onClick={() => removeTag(t)}
                  className="text-[#6B4954] hover:text-[#451C25]"
                  aria-label={`Remove tag ${t}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="kb-tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder="Add a tag and press Enter (e.g. d7, nlv, healthcare)"
              className="flex-1 bg-transparent border-b border-[#451C25]/20 py-2 text-sm focus:outline-none focus:border-[#451C25]"
            />
            <button
              data-testid="kb-tag-add"
              onClick={addTag}
              className="px-4 py-2 text-[11px] tracking-[0.18em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        <div className="mt-5">
          <TF
            label="Source URL (optional)"
            value={data.source_url || ""}
            on={(v) => upd("source_url", v)}
            testid="kb-source"
            placeholder="https://..."
          />
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="kb-save"
            onClick={() => onSave(data)}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
          >
            <Save size={14} /> Save article
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   AGENT PANEL (chat)
   ============================================================ */
function newSessionId() {
  return "s-" + Math.random().toString(36).slice(2) + "-" + Date.now().toString(36);
}

function AgentPanel({ axios }) {
  const [sessionId, setSessionId] = useState(() => newSessionId());
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [scope, setScope] = useState("All");
  const [submitting, setSubmitting] = useState(false);
  // Client linking
  const [mode, setMode] = useState("training"); // "training" | "client"
  const [clientId, setClientId] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [activeClient, setActiveClient] = useState(null);

  // Live search clients when user types in the picker
  useEffect(() => {
    if (mode !== "client") return;
    const term = clientSearch.trim();
    const t = setTimeout(() => {
      const params = term ? { search: term, limit: 12 } : { limit: 12 };
      axios
        .get(`/admin/consultations`, { params })
        .then((r) => setClientResults((r.data || []).slice(0, 12)))
        .catch(() => setClientResults([]));
    }, term ? 250 : 0);
    return () => clearTimeout(t);
  }, [clientSearch, mode, axios]);

  const pickClient = (c) => {
    setClientId(c.id);
    setActiveClient(c);
    setClientResults([]);
    setClientSearch("");
    // Reset session when switching client (fresh context)
    setSessionId(newSessionId());
    setMessages([]);
  };

  const clearClient = () => {
    setClientId(null);
    setActiveClient(null);
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    if (mode === "client" && !clientId) {
      toast.error("Pick a client first or switch to Training mode.");
      return;
    }
    setMessages((m) => [...m, { role: "user", content: text }]);
    setDraft("");
    setSubmitting(true);
    try {
      const res = await axios.post(`/admin/agent/chat`, {
        session_id: sessionId,
        message: text,
        country_scope: scope === "All" ? null : scope,
        mode,
        client_id: mode === "client" ? clientId : null,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.data.answer, sources: res.data.sources || [] },
      ]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Agent failed.";
      toast.error(typeof msg === "string" ? msg : "Agent failed.");
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "⚠️ " + (typeof msg === "string" ? msg : "Agent failed."), sources: [] },
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  const newConversation = () => {
    setSessionId(newSessionId());
    setMessages([]);
  };

  return (
    <div data-testid="agent-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <aside className="lg:col-span-3 space-y-5">
        <div>
          <h2 className="font-serif text-3xl tracking-tight">Ask the agent</h2>
          <p className="text-[#6B4954] text-sm mt-2 leading-relaxed">
            Internal research assistant powered by Claude Sonnet 4.5 grounded in our
            knowledge base.
          </p>
        </div>

        <div>
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Conversation mode
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="agent-mode-training"
              onClick={() => {
                setMode("training");
                clearClient();
                newConversation();
              }}
              className={`flex flex-col items-center py-3 px-2 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                mode === "training"
                  ? "bg-[#451C25] text-white border-[#451C25]"
                  : "border-[#451C25]/25 text-[#451C25] hover:border-[#451C25]"
              }`}
            >
              <GraduationCap size={16} className="mb-1.5" />
              Training
            </button>
            <button
              data-testid="agent-mode-client"
              onClick={() => {
                setMode("client");
                newConversation();
              }}
              className={`flex flex-col items-center py-3 px-2 text-[10px] tracking-[0.18em] uppercase border transition-colors ${
                mode === "client"
                  ? "bg-[#451C25] text-white border-[#451C25]"
                  : "border-[#451C25]/25 text-[#451C25] hover:border-[#451C25]"
              }`}
            >
              <User size={16} className="mb-1.5" />
              Client
            </button>
          </div>
        </div>

        {mode === "client" && (
          <div data-testid="agent-client-picker">
            <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
              Linked client
            </label>
            {activeClient ? (
              <div className="bg-white border border-[#451C25]/15 p-3">
                <div className="font-medium text-[14px]">{activeClient.full_name}</div>
                <div className="text-[#6B4954] text-xs">{activeClient.email}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#451C25] mt-1">
                  {activeClient.country_interest} · {STATUS_LABEL[activeClient.status]}
                </div>
                <button
                  data-testid="agent-client-clear"
                  onClick={clearClient}
                  className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#6B4954] hover:text-[#451C25]"
                >
                  Change client
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 border-b border-[#451C25]/20 focus-within:border-[#451C25]">
                  <Search size={14} className="text-[#6B4954]" />
                  <input
                    data-testid="agent-client-search"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search name or email..."
                    className="flex-1 bg-transparent py-2 text-sm focus:outline-none"
                  />
                </div>
                <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[#6B4954]">
                  {clientSearch.trim() ? "Results" : "Recent clients"}
                </div>
                {clientResults.length > 0 ? (
                  <ul className="mt-1 bg-white border border-[#451C25]/10 max-h-72 overflow-y-auto" data-testid="agent-client-results">
                    {clientResults.map((c) => (
                      <li key={c.id}>
                        <button
                          data-testid={`agent-client-option-${c.id}`}
                          onClick={() => pickClient(c)}
                          className="w-full text-left px-3 py-2 hover:bg-[#F5EFE7] border-b border-[#451C25]/10 last:border-b-0"
                        >
                          <div className="text-[13px] font-medium">{c.full_name}</div>
                          <div className="text-[#6B4954] text-[11px]">
                            {c.email} · {c.country_interest}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-1 bg-white border border-dashed border-[#451C25]/15 p-3 text-[11px] text-[#6B4954]">
                    No clients found.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div>
          <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
            Country scope
          </label>
          <select
            data-testid="agent-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="w-full bg-white border border-[#451C25]/20 py-2 px-3 text-sm focus:outline-none focus:border-[#451C25]"
          >
            {["All", "Spain", "Portugal", "Italy"].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <button
          data-testid="agent-new"
          onClick={newConversation}
          className="w-full inline-flex items-center justify-center gap-2 border border-[#451C25]/30 text-[#451C25] py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#451C25] hover:text-white transition-colors"
        >
          <Plus size={14} /> New conversation
        </button>
        <div className="text-[11px] text-[#6B4954] leading-relaxed border-t border-[#451C25]/15 pt-4">
          <strong className="block uppercase tracking-[0.22em] text-[#451C25] mb-2">
            Tip
          </strong>
          {mode === "client" ? (
            <>
              Client mode tailors the agent to this person's profile. The conversation
              gets saved on their record.
            </>
          ) : (
            <>
              Try: <em>"D7 vs NLV for a couple with $50k/year pension"</em>
            </>
          )}
        </div>
      </aside>

      <section className="lg:col-span-9">
        <div className="border border-[#451C25]/10 bg-white min-h-[60vh] flex flex-col">
          {activeClient && (
            <div className="px-6 py-3 bg-[#EDE4D6] border-b border-[#451C25]/10 text-[11px] tracking-[0.18em] uppercase text-[#6B4954] flex items-center gap-2">
              <User size={12} />
              Linked to client: <strong className="text-[#451C25] normal-case tracking-normal">{activeClient.full_name}</strong>
            </div>
          )}
          <div className="flex-1 p-6 md:p-8 overflow-y-auto space-y-6 max-h-[65vh]">
            {messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-[#6B4954] text-sm">
                {mode === "client" && !clientId
                  ? "Pick a client on the left or switch to Training mode."
                  : "Ask anything about Spain, Portugal, or Italy."}
              </div>
            ) : (
              messages.map((m, i) => <ChatBubble key={i} m={m} />)
            )}
            {submitting && (
              <div className="flex items-center gap-2 text-[#6B4954] text-sm">
                <span className="w-1.5 h-1.5 bg-[#451C25] rounded-full animate-bounce" />
                <span className="w-1.5 h-1.5 bg-[#451C25] rounded-full animate-bounce [animation-delay:120ms]" />
                <span className="w-1.5 h-1.5 bg-[#451C25] rounded-full animate-bounce [animation-delay:240ms]" />
                Thinking
              </div>
            )}
          </div>
          <div className="border-t border-[#451C25]/10 p-4 md:p-5 flex items-end gap-3">
            <textarea
              data-testid="agent-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Ask the agent... (Enter to send, Shift+Enter for newline)"
              rows={2}
              className="flex-1 bg-transparent border border-[#451C25]/15 p-3 text-sm focus:outline-none focus:border-[#451C25] resize-none"
            />
            <button
              data-testid="agent-send"
              onClick={submit}
              disabled={submitting || !draft.trim() || (mode === "client" && !clientId)}
              className="shrink-0 inline-flex items-center gap-2 bg-[#451C25] disabled:opacity-50 text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#2B1017] transition-colors"
            >
              <Send size={14} /> Send
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ChatBubble({ m }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end" data-testid="chat-user">
        <div className="max-w-[80%] bg-[#451C25] text-white px-4 py-3 rounded-sm">
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 mb-1">You</div>
          <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start" data-testid="chat-assistant">
      <div className="max-w-[90%] bg-[#F5EFE7] border border-[#451C25]/10 px-5 py-4 rounded-sm">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#451C25] mb-2 inline-flex items-center gap-1.5">
          <Sparkles size={11} /> Agent
        </div>
        <FormattedAnswer body={m.content} />
        {m.sources?.length > 0 && (
          <div
            data-testid="chat-sources"
            className="mt-4 pt-3 border-t border-[#451C25]/10"
          >
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
              Sources ({m.sources.length})
            </div>
            <ul className="space-y-1">
              {m.sources.map((s) => (
                <li key={s.id} className="text-[12px] text-[#6B4954]">
                  <span className="text-[#451C25]">{s.country}</span> ·{" "}
                  <span className="text-[#451C25]/70">{s.topic}</span> · {s.title}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function FormattedAnswer({ body }) {
  const blocks = (body || "").split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="text-[14px] text-[#451C25] leading-relaxed space-y-3">
      {blocks.map((b, i) => {
        if (b.startsWith("## ")) {
          return (
            <h4 key={i} className="font-serif text-lg tracking-tight mt-2">
              {b.replace(/^##\s+/, "")}
            </h4>
          );
        }
        if (b.startsWith("• ") || /^\d+\.\s/.test(b)) {
          const items = b.split("\n");
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 marker:text-[#451C25]">
              {items.map((it, j) => (
                <li key={j}>
                  <InlineFormat text={it.replace(/^•\s+/, "").replace(/^\d+\.\s+/, "")} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            <InlineFormat text={b} />
          </p>
        );
      })}
    </div>
  );
}

function InlineFormat({ text }) {
  const parts = (text || "").split(/(\*\*[^*]+\*\*|\[Article:[^\]]+\])/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          return (
            <strong key={i} className="font-medium text-[#451C25]">
              {p.slice(2, -2)}
            </strong>
          );
        }
        if (p.startsWith("[Article:") && p.endsWith("]")) {
          return (
            <span
              key={i}
              className="inline-block px-1.5 py-0.5 mx-0.5 text-[10px] tracking-wide uppercase bg-[#EDE4D6] text-[#451C25] rounded-sm"
            >
              {p.slice(9, -1).trim()}
            </span>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "Invalid input").join(", ");
  if (typeof d === "string") return d;
  return fallback;
}

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

function fmtSettingsDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function PwInput({ label, value, on, testid }) {
  return (
    <div>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2">
        {label}
      </label>
      <input
        type="password"
        data-testid={testid}
        value={value}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-transparent border-b border-[#451C25]/20 py-2 focus:outline-none focus:border-[#451C25] transition-colors"
      />
    </div>
  );
}

function SettingsPanel({ axios, currentEmail, onSelfRenamed }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newAdminPw, setNewAdminPw] = useState("");
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState("");

  const fetchAdmins = () => {
    setLoading(true);
    axios
      .get(`/admin/admins`)
      .then((r) => setAdmins(r.data || []))
      .catch(() => toast.error("Could not load administrators."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters.");
    if (newPw !== confirmPw) return toast.error("New passwords do not match.");
    setSavingPw(true);
    try {
      await axios.post(`/admin/change-password`, {
        current_password: curPw,
        new_password: newPw,
      });
      toast.success("Password updated.");
      setCurPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e2) {
      toast.error(errMsg(e2, "Could not update password."));
    } finally {
      setSavingPw(false);
    }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    if (!emailOk(newEmail.trim())) return toast.error("Enter a valid email address.");
    if (newAdminPw.length < 8) return toast.error("Password must be at least 8 characters.");
    setCreating(true);
    try {
      await axios.post(`/admin/admins`, { email: newEmail.trim(), password: newAdminPw });
      toast.success("Administrator added.");
      setNewEmail("");
      setNewAdminPw("");
      setShowAdd(false);
      fetchAdmins();
    } catch (e2) {
      toast.error(errMsg(e2, "Could not add administrator."));
    } finally {
      setCreating(false);
    }
  };

  const saveRename = async (a) => {
    const email = editEmail.trim().toLowerCase();
    if (!email || email === a.email) {
      setEditingId(null);
      return;
    }
    if (!emailOk(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    try {
      const r = await axios.patch(`/admin/admins/${a.id}`, { email });
      if (r.data?.self_renamed) {
        onSelfRenamed();
        return;
      }
      toast.success("Email updated.");
      setEditingId(null);
      fetchAdmins();
    } catch (e2) {
      toast.error(errMsg(e2, "Could not rename administrator."));
    }
  };

  const toggleActive = async (a) => {
    try {
      await axios.patch(`/admin/admins/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Administrator deactivated." : "Administrator activated.");
      fetchAdmins();
    } catch (e2) {
      toast.error(errMsg(e2, "Could not update administrator."));
    }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Delete admin ${a.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`/admin/admins/${a.id}`);
      toast.success("Administrator deleted.");
      fetchAdmins();
    } catch (e2) {
      toast.error(errMsg(e2, "Could not delete administrator."));
    }
  };

  return (
    <div data-testid="settings-panel" className="space-y-12">
      <div>
        <h2 className="font-serif text-3xl tracking-tight">My account</h2>
        <p className="text-[#6B4954] text-sm mt-1">Signed in as {currentEmail}</p>
        <form
          onSubmit={changePassword}
          data-testid="change-password-form"
          className="mt-5 bg-white border border-[#451C25]/10 p-6 max-w-md space-y-4"
        >
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
            Change password
          </div>
          <PwInput label="Current password" value={curPw} on={setCurPw} testid="cp-current" />
          <PwInput label="New password (min 8)" value={newPw} on={setNewPw} testid="cp-new" />
          <PwInput
            label="Confirm new password"
            value={confirmPw}
            on={setConfirmPw}
            testid="cp-confirm"
          />
          <button
            data-testid="cp-submit"
            type="submit"
            disabled={savingPw}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors disabled:opacity-50"
          >
            <KeyRound size={14} /> {savingPw ? "Saving…" : "Update password"}
          </button>
        </form>
      </div>

      <div>
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-serif text-3xl tracking-tight">Administrators</h2>
            <p className="text-[#6B4954] text-sm mt-1">
              {admins.length} accounts · every admin has full access
            </p>
          </div>
          <button
            data-testid="admin-add-btn"
            onClick={() => setShowAdd((s) => !s)}
            className="inline-flex items-center gap-2 bg-[#451C25] text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors"
          >
            <UserPlus size={14} /> Add admin
          </button>
        </div>

        {showAdd && (
          <form
            onSubmit={createAdmin}
            data-testid="admin-add-form"
            className="bg-white border border-[#451C25]/10 p-6 mb-5 grid md:grid-cols-[1fr_1fr_auto] gap-4 items-end"
          >
            <TF
              label="Email"
              value={newEmail}
              on={setNewEmail}
              testid="admin-add-email"
              placeholder="name@destinoeurope.com"
            />
            <PwInput
              label="Password (min 8)"
              value={newAdminPw}
              on={setNewAdminPw}
              testid="admin-add-password"
            />
            <button
              data-testid="admin-add-submit"
              type="submit"
              disabled={creating}
              className="inline-flex items-center justify-center gap-2 bg-[#451C25] text-white px-5 h-[42px] text-[11px] tracking-[0.22em] uppercase font-medium hover:bg-[#2B1017] transition-colors disabled:opacity-50"
            >
              <Save size={14} /> {creating ? "Adding…" : "Create"}
            </button>
          </form>
        )}

        {loading ? (
          <div className="h-40 bg-white border border-[#451C25]/10 animate-pulse" />
        ) : (
          <div className="overflow-x-auto border border-[#451C25]/10 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#EDE4D6] text-left">
                <tr>
                  <Th>Email</Th>
                  <Th>Status</Th>
                  <Th>Last login</Th>
                  <Th>Added</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`admin-row-${a.id}`}
                    className="border-t border-[#451C25]/10"
                  >
                    <Td>
                      {editingId === a.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`admin-rename-input-${a.id}`}
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            className="bg-transparent border-b border-[#451C25]/30 py-1 focus:outline-none focus:border-[#451C25] min-w-[220px]"
                          />
                          <button
                            data-testid={`admin-rename-save-${a.id}`}
                            onClick={() => saveRename(a)}
                            className="text-[#183129] hover:opacity-70"
                            title="Save"
                          >
                            <Save size={15} />
                          </button>
                          <button
                            data-testid={`admin-rename-cancel-${a.id}`}
                            onClick={() => setEditingId(null)}
                            className="text-[#6B4954] hover:opacity-70"
                            title="Cancel"
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ) : (
                        <span className="font-medium">
                          {a.email}
                          {a.is_self && (
                            <span className="ml-2 text-[10px] tracking-[0.18em] uppercase text-[#897756]">
                              (you)
                            </span>
                          )}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] ${
                          a.active
                            ? "bg-[#183129]/10 text-[#183129]"
                            : "bg-[#451C25]/10 text-[#6B4954]"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            a.active ? "bg-[#183129]" : "bg-[#9b8b7e]"
                          }`}
                        />
                        {a.active ? "Active" : "Inactive"}
                      </span>
                    </Td>
                    <Td className="text-[#6B4954]">{fmtSettingsDateTime(a.last_login)}</Td>
                    <Td className="text-[#6B4954]">{fmtSettingsDateTime(a.created_at)}</Td>
                    <Td>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`admin-rename-${a.id}`}
                          title="Rename email"
                          onClick={() => {
                            setEditingId(a.id);
                            setEditEmail(a.email);
                          }}
                          className="text-[#451C25] hover:opacity-70"
                        >
                          <Pencil size={15} />
                        </button>
                        {!a.is_self && (
                          <button
                            data-testid={`admin-toggle-${a.id}`}
                            title={a.active ? "Deactivate" : "Activate"}
                            onClick={() => toggleActive(a)}
                            className="text-[#451C25] hover:opacity-70"
                          >
                            <Power size={15} />
                          </button>
                        )}
                        {!a.is_self && (
                          <button
                            data-testid={`admin-delete-${a.id}`}
                            title="Delete"
                            onClick={() => removeAdmin(a)}
                            className="text-[#9b2c3f] hover:opacity-70"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PillSelect({ label, value, options, on, testid, renderLabel }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2">
      <span className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o}
            data-testid={`${testid}-${String(o).toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => on(o)}
            className={`px-3 py-1 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
              value === o
                ? "bg-[#451C25] text-white border-[#451C25]"
                : "border-[#451C25]/25 text-[#451C25] hover:border-[#451C25]"
            }`}
          >
            {renderLabel ? renderLabel(o) : o}
          </button>
        ))}
      </div>
    </div>
  );
}
